/**
 * DALI bus vocabulary.
 *
 * The firmware validates the master and the mode but not the address or the
 * parameter: the address ranges below are protocol constants, so an out-of-range
 * value is accepted by the parser and simply fails on the bus. The console
 * therefore offers the ranges rather than trusting a technician to remember them.
 */

/** Bus masters. A bridge occupies one; this app models up to sixteen. */
export const DALI_MASTER = {
  MIN: 0,
  MAX: 15,
  /** Used until the technician picks another. */
  DEFAULT: 0,
} as const;

/** `mode` in `dalioperate`. */
export const DALI_MODE = {
  /** Ask for the current level; send `param` 0. */
  QUERY: 0,
  /** Set a level; `param` is the level. */
  DIM: 1,
  /** Set a colour temperature; `param` is the value. */
  TEMP: 2,
} as const;

export type DaliMode = (typeof DALI_MODE)[keyof typeof DALI_MODE];

/** Modes in the order the console offers them. */
export const DALI_MODES = [DALI_MODE.QUERY, DALI_MODE.DIM, DALI_MODE.TEMP] as const;

const isDaliMode = (value: number): value is DaliMode =>
  DALI_MODES.some((candidate) => candidate === value);

/** The name a `daliack` uses for each mode. */
export const DALI_MODE_NAME = {
  [DALI_MODE.QUERY]: 'query',
  [DALI_MODE.DIM]: 'dim',
  [DALI_MODE.TEMP]: 'temp',
} as const satisfies Record<DaliMode, string>;

/** Not a firmware mode: what the console shows for a mode it does not model. */
export const DALI_MODE_UNKNOWN = 'unknown';

export type DaliModeName =
  | (typeof DALI_MODE_NAME)[keyof typeof DALI_MODE_NAME]
  | typeof DALI_MODE_UNKNOWN;

/** Label of each mode, in the order the console offers them. */
export const DALI_MODE_LABEL = {
  query: 'Query',
  dim: 'Dim',
  temp: 'Temperature',
  unknown: 'Unknown',
} as const satisfies Record<DaliModeName, string>;

/** The name of a numeric mode, for typing a reply's `mode` field. */
export const daliModeName = (mode: DaliMode): DaliModeName => DALI_MODE_NAME[mode];

/** Which mode a `daliack`'s `mode` string refers to. */
export const daliModeFromName = (name: string): DaliModeName =>
  Object.values(DALI_MODE_NAME).find((candidate) => candidate === name) ?? DALI_MODE_UNKNOWN;

/** Which mode number a console selection means, for a payload preview. */
export const daliModeNumber = (name: DaliModeName): DaliMode | null => {
  const entry = Object.entries(DALI_MODE_NAME).find(([, value]) => value === name);
  if (entry === undefined) return null;
  const mode = Number.parseInt(entry[0], 10);
  return isDaliMode(mode) ? mode : null;
};

/** Human label of a numeric mode, falling back to the number itself. */
export const daliModeLabel = (mode: number): string =>
  isDaliMode(mode) ? DALI_MODE_LABEL[DALI_MODE_NAME[mode]] : `Mode ${mode}`;

/**
 * Address ranges. Individual addresses are 0–64, group addresses 128–159, and
 * 255 broadcasts to the whole bus.
 */
export const DALI_ADDRESS = {
  INDIVIDUAL_MIN: 0,
  INDIVIDUAL_MAX: 64,
  GROUP_MIN: 128,
  GROUP_MAX: 159,
  BROADCAST: 255,
} as const;

/** Short addresses on one master, which is what a bridge exposes. */
export const DALI_ADDRESS_COUNT = DALI_ADDRESS.INDIVIDUAL_MAX + 1;

/** Address kinds the console offers, with the range each one covers. */
export const DALI_ADDRESS_KIND = {
  INDIVIDUAL: 'individual',
  GROUP: 'group',
  BROADCAST: 'broadcast',
} as const;

export type DaliAddressKind = (typeof DALI_ADDRESS_KIND)[keyof typeof DALI_ADDRESS_KIND];

/** `param`, a byte: a dim level or a colour temperature. */
export const DALI_PARAM = {
  MIN: 0,
  MAX: 255,
  /** Sent with a query. */
  QUERY: 0,
} as const;

/** What a failed `daliack` reports in `value`. */
export const DALI_NAK = {
  NO_RESPONSE: 1,
  INVALID_ADDRESS: 2,
  FAILED: 3,
  BUSY: 4,
  UNSUPPORTED: 5,
} as const;

const NAK_LABELS: Readonly<Record<number, string>> = {
  [DALI_NAK.NO_RESPONSE]: 'No response from the ballast.',
  [DALI_NAK.INVALID_ADDRESS]: 'Invalid address.',
  [DALI_NAK.FAILED]: 'The command failed.',
  [DALI_NAK.BUSY]: 'The bus is busy.',
  [DALI_NAK.UNSUPPORTED]: 'Unsupported by this ballast.',
};

/** What a NAK value means, or an empty string when it is not one. */
export const daliNakLabel = (value: number): string => NAK_LABELS[value] ?? '';

/** A DALI bridge is addressed like a rail module: a 4-bit rotary address. */
export const DALI_BRIDGE_ADDRESS = {
  MIN: 0,
  MAX: 15,
} as const;

/** The bridge's own label, shown when a technician has not written one. */
export const daliBridgeLabel = (address: number, master: number): string =>
  `Bridge ${address} · master ${master}`;
